/**
 * useBudget - Hook for managing budget/CapEx state
 *
 * Extracted from FinancialAnalysis.js to be shared between
 * the Budget tab and the Results/Financials tab.
 */

import { useState, useMemo, useEffect, useCallback } from 'react';
import useCapexRates from './useCapexRates';
import { debug } from '../utils/debug';
import { calculateCapex as calcCapex, calculateItc as calcItc, calculateOpex as calcOpex } from '../utils/calculations';

/**
 * Hook Props:
 * @param {Object} systemSizes - Object with pvKw, batteryKw, batteryKwh, generatorKw
 * @param {Object} selectedBatteryData - Selected battery from DB with custom pricing
 * @param {Object} selectedSolarData - Selected solar equipment from DB with custom pricing
 * @param {Object} selectedGeneratorData - Selected generator from DB with custom pricing
 * @param {Object} selectedSwitchgearData - Selected switchgear from DB with custom pricing
 * @param {Object} initialState - Optional initial state for restoration from saved scenario
 */
function useBudget({
    systemSizes = {},
    selectedBatteryData = null,
    selectedSolarData = null,
    selectedGeneratorData = null,
    selectedSwitchgearData = null,
    initialState = null
} = {}) {
    // Fetch admin-managed CapEx default rates
    const capexRates = useCapexRates();

    // CAPEX state - equipment, installation, soft costs
    const [capexInputs, setCapexInputs] = useState(initialState?.capexInputs || {
        pvTurnkey: { costType: 'per_kw', rate: 1800, totalCost: 0 },
        battery: { costType: 'per_kwh', rate: 350, totalCost: 0 },
        generator: { costType: 'per_kw', rate: 800, totalCost: 0 },
        installation: {
            battery: { costType: 'per_kwh', rate: 100, totalCost: 0 },
            generator: { costType: 'per_kw', rate: 200, totalCost: 0 }
        },
        softCosts: {
            engineering: { costType: 'percentage', rate: 5, totalCost: 0 },
            permits: { costType: 'percentage', rate: 3, totalCost: 0 },
            projectManagement: { costType: 'percentage', rate: 4, totalCost: 0 },
            commissioning: { costType: 'percentage', rate: 1, totalCost: 0 },
            contingency: { costType: 'percentage', rate: 5, totalCost: 0 },
            custom: { costType: 'lump_sum', rate: 0, totalCost: 0, label: '' }
        }
    });

    // ITC (Investment Tax Credit) percentages per line item
    const [itcInputs, setItcInputs] = useState(initialState?.itcInputs || {
        pvTurnkey: 30,
        batteryEquipment: 30,
        generatorEquipment: 0,
        batteryInstallation: 30,
        generatorInstallation: 0,
        engineering: 30,
        permits: 30,
        projectManagement: 30,
        commissioning: 30,
        contingency: 30,
        custom: 0,
    });

    // ITC toggle (enable/disable federal incentives)
    const [includeItc, setIncludeItc] = useState(initialState?.includeItc ?? true);

    // CapEx source: 'nrel' (REopt defaults) or 'custom' (user-edited)
    const [capexSource, setCapexSource] = useState(initialState?.capexSource || 'custom');

    // OPEX state - annual operating and maintenance costs
    const [opexInputs, setOpexInputs] = useState(initialState?.opexInputs || {
        pv: { rate: 20, costType: 'per_kw_year' }, // $/kW/year
        battery: { rate: 10, costType: 'per_kwh_year' }, // $/kWh/year
        generator: { rate: 15, costType: 'per_kw_year' }, // $/kW/year
        fixedAnnual: { rate: 0, costType: 'lump_sum' }, // Fixed annual O&M
        custom: { rate: 0, costType: 'lump_sum', label: '' }, // Custom O&M line item
        fuel: {
            dieselPrice: 3.50, // $/gallon
            naturalGasPrice: 0.50, // $/therm
            dieselEfficiency: 13.5, // kWh/gallon
            naturalGasEfficiency: 11.0 // kWh/therm
        }
    });

    // Track if we've already restored from initialState to prevent overwriting user edits
    const [hasRestoredInitialState, setHasRestoredInitialState] = useState(false);

    // CRITICAL: Restore state when initialState becomes available (async scenario load)
    // This handles the case where useBudget mounts before scenario data is loaded
    useEffect(() => {
        if (initialState && !hasRestoredInitialState) {
            debug.log('💰 [useBudget] Restoring budget state from initialState:', initialState);
            if (initialState.capexInputs) setCapexInputs(initialState.capexInputs);
            if (initialState.itcInputs) setItcInputs(initialState.itcInputs);
            if (initialState.includeItc !== undefined) setIncludeItc(initialState.includeItc);
            if (initialState.capexSource) setCapexSource(initialState.capexSource);
            if (initialState.opexInputs) setOpexInputs(initialState.opexInputs);

            setHasRestoredInitialState(true);
        }
    }, [initialState, hasRestoredInitialState]);

    // Initialize CAPEX inputs with admin-managed defaults when rates load
    // Prefer equipment database costs over generic rates
    // Also update when equipment selection changes
    useEffect(() => {
        const hasBatterySelection = Array.isArray(selectedBatteryData) ? selectedBatteryData.length > 0 : !!selectedBatteryData;
        const hasEquipmentSelection = selectedSolarData || hasBatterySelection || selectedGeneratorData || selectedSwitchgearData;

        if (!capexRates.loading && ((!initialState && !hasRestoredInitialState) || hasEquipmentSelection)) {
            // Solar: Use equipment database turnkey_cost_per_kw or fallback to generic rate
            const pvTurnkeyRate = selectedSolarData?.turnkey_cost_per_kw ?? capexRates.pvTurnkeyRate;

            // Battery: Use equipment database costs or fallback to generic rates
            // Supports both array (multi-battery) and single object (legacy) formats
            let batteryEquipmentRate = capexRates.batteryEquipmentRate;
            let batteryInstallRate = capexRates.batteryInstallRate;
            if (Array.isArray(selectedBatteryData) && selectedBatteryData.length > 0) {
                const totalKwh = selectedBatteryData.reduce((s, b) => s + (b.kwh || b.capacity_kwh || 0) * (b.qty || 1), 0);
                if (totalKwh > 0) {
                    // Per-kWh rate: prefer cost_per_kwh, fall back to equipment_cost / capacity
                    const perKwh = (b) => b.cost_per_kwh || (b.equipment_cost && (b.kwh || b.capacity_kwh) ? b.equipment_cost / (b.kwh || b.capacity_kwh) : 0);
                    const installPerKwh = (b) => b.install_cost_per_kwh || (b.installation_cost && (b.kwh || b.capacity_kwh) ? b.installation_cost / (b.kwh || b.capacity_kwh) : 0);
                    batteryEquipmentRate = selectedBatteryData.reduce((s, b) => s + perKwh(b) * (b.kwh || b.capacity_kwh || 0) * (b.qty || 1), 0) / totalKwh;
                    batteryInstallRate = selectedBatteryData.reduce((s, b) => s + installPerKwh(b) * (b.kwh || b.capacity_kwh || 0) * (b.qty || 1), 0) / totalKwh;
                }
            } else if (selectedBatteryData && !Array.isArray(selectedBatteryData)) {
                batteryEquipmentRate = selectedBatteryData.equipment_cost
                    ? (selectedBatteryData.equipment_cost / selectedBatteryData.capacity_kwh)
                    : (selectedBatteryData.cost_per_kwh ?? batteryEquipmentRate);
                batteryInstallRate = selectedBatteryData.installation_cost
                    ? (selectedBatteryData.installation_cost / selectedBatteryData.capacity_kwh)
                    : (selectedBatteryData.install_cost_per_kwh ?? batteryInstallRate);
            }

            // Battery O&M: Use equipment database om_cost_per_kwh_year if available
            let batteryOMRate = null;
            if (Array.isArray(selectedBatteryData) && selectedBatteryData.length > 0) {
                const totalKwh = selectedBatteryData.reduce((s, b) => s + (b.kwh || b.capacity_kwh || 0) * (b.qty || 1), 0);
                if (totalKwh > 0) {
                    const omPerKwh = (b) => b.om_cost_per_kwh_year || 0;
                    const weightedOM = selectedBatteryData.reduce((s, b) => s + omPerKwh(b) * (b.kwh || b.capacity_kwh || 0) * (b.qty || 1), 0) / totalKwh;
                    if (weightedOM > 0) batteryOMRate = weightedOM;
                }
            } else if (selectedBatteryData && !Array.isArray(selectedBatteryData)) {
                if (selectedBatteryData.om_cost_per_kwh_year) {
                    batteryOMRate = selectedBatteryData.om_cost_per_kwh_year;
                }
            }

            // Generator: Use equipment database costs or fallback to generic rates
            const generatorEquipmentRate = selectedGeneratorData?.equipment_cost
                ? (selectedGeneratorData.equipment_cost / selectedGeneratorData.power_kw)
                : capexRates.generatorEquipmentRate;
            const generatorInstallRate = selectedGeneratorData?.installation_cost
                ? (selectedGeneratorData.installation_cost / selectedGeneratorData.power_kw)
                : capexRates.generatorInstallRate;

            debug.log('💰 [useBudget] Calculating rates from equipment:', {
                selectedSolar: selectedSolarData ? `${selectedSolarData.manufacturer} ${selectedSolarData.model}` : 'none',
                pvTurnkeyRate,
                selectedBattery: Array.isArray(selectedBatteryData) ? `${selectedBatteryData.length} model(s)` : (selectedBatteryData ? `${selectedBatteryData.manufacturer} ${selectedBatteryData.model}` : 'none'),
                batteryEquipmentRate,
                batteryInstallRate,
                selectedGenerator: selectedGeneratorData ? `${selectedGeneratorData.manufacturer} ${selectedGeneratorData.model}` : 'none',
                generatorEquipmentRate,
                generatorInstallRate
            });

            const softCosts = capexRates.softCostRates;

            setCapexInputs(prev => ({
                ...prev,
                // Only update if rate is not null
                pvTurnkey: { ...prev.pvTurnkey, rate: pvTurnkeyRate ?? prev.pvTurnkey.rate },
                battery: { ...prev.battery, rate: batteryEquipmentRate ?? prev.battery.rate },
                generator: { ...prev.generator, rate: generatorEquipmentRate ?? prev.generator.rate },
                installation: {
                    battery: { ...prev.installation.battery, rate: batteryInstallRate ?? prev.installation.battery.rate },
                    generator: { ...prev.installation.generator, rate: generatorInstallRate ?? prev.installation.generator.rate }
                },
                softCosts: {
                    engineering: { ...prev.softCosts.engineering, rate: softCosts.engineering ?? prev.softCosts.engineering.rate },
                    permits: { ...prev.softCosts.permits, rate: softCosts.permits ?? prev.softCosts.permits.rate },
                    projectManagement: { ...prev.softCosts.projectManagement, rate: softCosts.projectManagement ?? prev.softCosts.projectManagement.rate },
                    commissioning: { ...prev.softCosts.commissioning, rate: softCosts.insurance ?? prev.softCosts.commissioning.rate },
                    contingency: { ...prev.softCosts.contingency, rate: softCosts.contingency ?? prev.softCosts.contingency.rate }
                }
            }));

            // Update OpEx battery rate from equipment database if available
            if (batteryOMRate !== null) {
                setOpexInputs(prev => ({
                    ...prev,
                    battery: { ...prev.battery, rate: batteryOMRate }
                }));
            }
        }
    }, [capexRates.loading, capexRates.pvTurnkeyRate, capexRates.batteryEquipmentRate, capexRates.batteryInstallRate, capexRates.generatorEquipmentRate, capexRates.generatorInstallRate, capexRates.softCostRates, selectedBatteryData, selectedSolarData, selectedGeneratorData, selectedSwitchgearData, initialState, hasRestoredInitialState]);

    // CAPEX calculations (based on system sizes)
    // Delegates to consolidated calculation utilities
    const capexCalculations = useMemo(() => {
        return calcCapex(systemSizes, capexInputs);
    }, [systemSizes, capexInputs]);

    // ITC calculations
    // Delegates to consolidated calculation utilities
    const itcCalculations = useMemo(() => {
        return calcItc(capexCalculations, itcInputs, includeItc, systemSizes);
    }, [capexCalculations, itcInputs, includeItc, systemSizes]);

    // OPEX calculations (annual operating costs)
    // Delegates to consolidated calculation utilities
    const opexCalculations = useMemo(() => {
        return calcOpex(systemSizes, opexInputs);
    }, [systemSizes, opexInputs]);

    // Update handlers
    const updateCapexInput = useCallback((category, field, value) => {
        setCapexInputs(prev => {
            const newInputs = { ...prev };
            if (field === 'costType') {
                newInputs[category] = {
                    ...prev[category],
                    [field]: value,
                    totalCost: value === 'lump_sum' ? (capexCalculations.components?.[category]?.total || capexCalculations.components?.[category]?.equipment || 0) : prev[category]?.totalCost
                };
            } else {
                newInputs[category] = {
                    ...prev[category],
                    [field]: typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) || 0 : value
                };
            }
            return newInputs;
        });
    }, [capexCalculations]);

    const updateInstallationCost = useCallback((category, field, value) => {
        setCapexInputs(prev => {
            const newInputs = { ...prev };
            if (field === 'costType') {
                newInputs.installation[category] = {
                    ...prev.installation[category],
                    [field]: value,
                    totalCost: value === 'lump_sum' ? (capexCalculations.components?.[category]?.installation || 0) : prev.installation[category]?.totalCost
                };
            } else {
                newInputs.installation[category] = {
                    ...prev.installation[category],
                    [field]: typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) || 0 : value
                };
            }
            return newInputs;
        });
    }, [capexCalculations]);

    const updateSoftCost = useCallback((category, field, value) => {
        setCapexInputs(prev => {
            const newInputs = { ...prev };
            if (field === 'costType') {
                newInputs.softCosts[category] = {
                    ...prev.softCosts[category],
                    [field]: value,
                    totalCost: value === 'lump_sum' ? (capexCalculations.softCosts?.[category] || 0) : prev.softCosts[category]?.totalCost
                };
            } else if (field === 'label') {
                newInputs.softCosts[category] = {
                    ...prev.softCosts[category],
                    label: value
                };
            } else {
                newInputs.softCosts[category] = {
                    ...prev.softCosts[category],
                    [field]: typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) || 0 : value
                };
            }
            return newInputs;
        });
    }, [capexCalculations]);

    const updateItcInput = useCallback((key, value) => {
        setItcInputs(prev => ({ ...prev, [key]: parseFloat(value) || 0 }));
    }, []);

    const updateOpexInput = useCallback((category, field, value) => {
        setOpexInputs(prev => {
            const newInputs = { ...prev };
            if (category === 'fuel') {
                newInputs.fuel = {
                    ...prev.fuel,
                    [field]: typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) || 0 : value
                };
            } else if (field === 'label') {
                newInputs[category] = {
                    ...prev[category],
                    label: value
                };
            } else {
                newInputs[category] = {
                    ...prev[category],
                    [field]: typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) || 0 : value
                };
            }
            return newInputs;
        });
    }, []);

    // Serialize state for saving to scenario
    const serializeBudget = useCallback(() => {
        return {
            capexInputs,
            itcInputs,
            includeItc,
            capexSource,
            opexInputs,
            // Include computed opex calculations for scenario comparison modal
            opexCalculations
        };
    }, [capexInputs, itcInputs, includeItc, capexSource, opexInputs, opexCalculations]);

    return {
        // CapEx State
        capexInputs,
        setCapexInputs,
        itcInputs,
        setItcInputs,
        includeItc,
        setIncludeItc,
        capexSource,
        setCapexSource,

        // OpEx State
        opexInputs,
        setOpexInputs,

        // Calculations
        capexCalculations,
        itcCalculations,
        opexCalculations,

        // Update handlers
        updateCapexInput,
        updateInstallationCost,
        updateSoftCost,
        updateItcInput,
        updateOpexInput,

        // Serialization
        serializeBudget,

        // Loading state - true if:
        // 1. Admin capex rates are still loading, OR
        // 2. We have initialState pending restoration (scenario budget not yet applied)
        // This prevents showing stale/default data before the saved scenario budget is restored
        loading: capexRates.loading || (initialState && !hasRestoredInitialState)
    };
}

export default useBudget;
